/**
 * Default study plans — seeded to Firestore siteSettings/pricing
 * so admins can edit price, discount, and marketing content.
 */
const AI_PLAN_FEATURES = [
  'Full GED course content',
  'Course explanation by AI',
  'GED AI tutor — 24/7',
  'System available 24/7',
  'AI quizzes system',
  'AI practice exams system',
];

const AI_PLAN_VALUE_PROP =
  'The GED final exam is also AI-based — preparing with our AI system makes your preparation 100% so you clear your GED in the first attempt.';

export const DEFAULT_STUDY_PLANS = [
  {
    id: '1-month',
    name: '1 Month GED Subscription',
    durationLabel: '30 days of full access',
    days: 30,
    regularPrice: 200,
    discountPercent: 50,
    badge: 'Most popular',
    featured: true,
    description: AI_PLAN_VALUE_PROP,
    features: AI_PLAN_FEATURES,
  },
  {
    id: '2-month',
    name: '2 Month GED Subscription',
    durationLabel: '60 days of full access',
    days: 60,
    regularPrice: 360,
    discountPercent: 50,
    badge: '',
    featured: false,
    description: AI_PLAN_VALUE_PROP,
    features: AI_PLAN_FEATURES,
  },
  {
    id: '3-month',
    name: '3 Month GED Subscription',
    durationLabel: '90 days of full access',
    days: 90,
    regularPrice: 480,
    discountPercent: 50,
    badge: 'Best value',
    featured: false,
    description: AI_PLAN_VALUE_PROP,
    features: AI_PLAN_FEATURES,
  },
];

/** "Need more days?" extension add-ons — admin-editable in siteSettings/pricing */
export const DEFAULT_EXTENSION_OPTIONS = [
  { days: 10, price: 30 },
  { days: 15, price: 35 },
  { days: 20, price: 40 },
  { days: 25, price: 45 },
  { days: 30, price: 50 },
];

export function normalizeExtensionOptions(options) {
  const list = Array.isArray(options) && options.length > 0 ? options : DEFAULT_EXTENSION_OPTIONS;
  return list
    .map((o) => ({
      days: Math.max(1, Math.round(Number(o.days) || 0)),
      price: Math.max(0, Number(o.price) || 0),
    }))
    .filter((o) => o.days > 0)
    .sort((a, b) => a.days - b.days);
}

/** Empty by default — admin sets a YouTube/embed URL; UI shows a placeholder until then */
export const DEFAULT_DEMO_VIDEO_URL = '';

/** GED exam fee — both amounts apply per selected subject (admin-editable) */
export const DEFAULT_EXAM_FEE = {
  perSubjectUsd: 90,
  serviceAndTaxUsd: 30,
};

/** Diploma & transcript charges (admin-editable) */
export const DEFAULT_DEGREE_FEE = {
  diplomaTranscriptUsd: 140,
  serviceAndTaxUsd: 40,
};

export const DEFAULT_ADMIN_CONTACT_PHONE = '+44 7345 060051';
export const DEFAULT_ADMIN_CONTACT_EMAIL = 'successbridgeacademy123@gmail.com';

export const GED_EXAM_SUBJECTS = [
  { id: 'math', name: 'Mathematical Reasoning' },
  { id: 'science', name: 'Science' },
  { id: 'social', name: 'Social Studies' },
  { id: 'rla', name: 'Reasoning Through Language Arts' },
];

export function normalizeExamFee(fee) {
  const src = fee && typeof fee === 'object' ? fee : {};
  return {
    perSubjectUsd: Math.max(0, Number(src.perSubjectUsd) || DEFAULT_EXAM_FEE.perSubjectUsd),
    serviceAndTaxUsd: Math.max(0, Number(src.serviceAndTaxUsd) || DEFAULT_EXAM_FEE.serviceAndTaxUsd),
  };
}

export function normalizeDegreeFee(fee) {
  const src = fee && typeof fee === 'object' ? fee : {};
  return {
    diplomaTranscriptUsd: Math.max(
      0,
      Number(src.diplomaTranscriptUsd) || DEFAULT_DEGREE_FEE.diplomaTranscriptUsd
    ),
    serviceAndTaxUsd: Math.max(0, Number(src.serviceAndTaxUsd) || DEFAULT_DEGREE_FEE.serviceAndTaxUsd),
  };
}

/** Total for N exam subjects: N × (fee + tax) — both charged per exam */
export function computeExamCheckoutTotal(subjectCount, examFee = DEFAULT_EXAM_FEE) {
  const n = Math.max(0, Math.round(Number(subjectCount) || 0));
  const fee = normalizeExamFee(examFee);
  const perSubject = fee.perSubjectUsd + fee.serviceAndTaxUsd;
  const examSubtotal = n * fee.perSubjectUsd;
  const taxSubtotal = n * fee.serviceAndTaxUsd;
  return {
    subjectCount: n,
    perSubjectFee: fee.perSubjectUsd,
    perSubjectTax: fee.serviceAndTaxUsd,
    examSubtotal,
    taxSubtotal,
    total: n * perSubject,
  };
}

export function computeDegreeCheckoutTotal(degreeFee = DEFAULT_DEGREE_FEE) {
  const fee = normalizeDegreeFee(degreeFee);
  return {
    diplomaTranscriptUsd: fee.diplomaTranscriptUsd,
    serviceAndTaxUsd: fee.serviceAndTaxUsd,
    total: fee.diplomaTranscriptUsd + fee.serviceAndTaxUsd,
  };
}

export function computeSalePrice(regularPrice, discountPercent) {
  const regular = Number(regularPrice) || 0;
  const discount = Math.min(100, Math.max(0, Number(discountPercent) || 0));
  return Math.round(regular * (1 - discount / 100) * 100) / 100;
}

export function normalizePlan(plan) {
  const regularPrice = Number(plan.regularPrice) || 0;
  const discountPercent = Math.min(100, Math.max(0, Number(plan.discountPercent) || 0));
  const days = Math.max(1, Number(plan.days) || 30);
  return {
    id: plan.id || `plan-${days}`,
    name: (plan.name || '').trim() || `${days} Day Study Plan`,
    durationLabel: (plan.durationLabel || '').trim() || `${days} days of full access`,
    days,
    regularPrice,
    discountPercent,
    price: computeSalePrice(regularPrice, discountPercent),
    badge: (plan.badge || '').trim() || null,
    featured: Boolean(plan.featured),
    description: (plan.description || '').trim(),
    features: Array.isArray(plan.features)
      ? plan.features.map((f) => String(f).trim()).filter(Boolean)
      : [],
  };
}

export function normalizePlans(plans) {
  const list = Array.isArray(plans) && plans.length > 0 ? plans : DEFAULT_STUDY_PLANS;
  return list.map(normalizePlan);
}

/** @deprecated use DEFAULT_STUDY_PLANS + useStudyPlans */
export const STUDY_PLANS = DEFAULT_STUDY_PLANS.map(normalizePlan);

export function formatUsd(amount) {
  return `$${Number(amount).toLocaleString('en-US', {
    minimumFractionDigits: Number(amount) % 1 ? 2 : 0,
    maximumFractionDigits: 2,
  })}`;
}
